import { Ref, RefString, RefBool } from "@tblabs/truffle";
import { DeliveryForm } from "../../Models/Basket/DeliveryForm";
import { ReturnForm } from "../../Models/Basket/ReturnForm";
import { PaymentPresets } from "../../Models/OrderTicket/PaymentForm";
import { RefWatcher } from "../../Utils/RefWatcher";
import { CustomerInfo } from "./CustomerInfo";
import { Basket } from "../../Services/Basket/Basket";
import { SelfTrainingDiscount } from "../../Services/Basket/SelfTrainingDiscount";
import { CryptoDiscount } from "../../Services/Basket/CryptoDiscount";


export class BasketFormLogic
{
    public Customer = new CustomerInfo();
    public PaymentForm = new Ref<PaymentPresets>(PaymentPresets.DepositWithBankTransfer_ServiceWithCash).Storable("payment-form");
    public DeliveryForm = new Ref<DeliveryForm>(DeliveryForm.Package).Storable("delivery-form");
    public ReturnForm = new Ref<ReturnForm>(ReturnForm.Package).Storable("return-form");
    public DepositPaymentInfo = new RefString();
    public DepositReturnInfo = new RefString();
    public TrainingInfo = new RefString();
    public WithTraining = new RefBool();
    public AllowCashPayment = new RefBool();
    public AllowCashAtDelivery = new RefBool();
    public ShowCustomerDepositAccountInput = new RefBool();
    public PickupSpot = new RefString("Warszawa Wola"); // NOT USED YET
    public IsValid = new RefBool();
    public AllowCryptoPayment = new RefBool();
    public AllowMixedPayment = new RefBool();
    public Notes = new RefString().Storable("customer-notes");

    public Clear()
    {
        this.Customer.Name.value = "";
        this.Customer.Address.Street.value = "";
        this.Customer.Address.PostalCode.value = "";
        this.Customer.Address.City.value = "";
        this.Customer.Email.value = "";
        this.Customer.Phone.value = "";
        this.Customer.Experience.value = 0;
        this.Customer.DepositAccount.value = "";
        
        window.localStorage.removeItem("customer-name");
        window.localStorage.removeItem("customer-email");
        window.localStorage.removeItem("customer-phone");
        window.localStorage.removeItem("customer-experience");
        window.localStorage.removeItem("customer-address-street");
        window.localStorage.removeItem("customer-address-postal-code");
        window.localStorage.removeItem("customer-address-city");
        window.localStorage.removeItem("customer-deposit-account");

        this.PaymentForm.value = PaymentPresets.Unset;
        this.DeliveryForm.value = DeliveryForm.Unset;
        this.ReturnForm.value = ReturnForm.Unset;
        this.DepositPaymentInfo.value = "";
        this.DepositReturnInfo.value = "";
        this.TrainingInfo.value = "";
        this.WithTraining.value = false;
        this.AllowCashPayment.value = false;
    }

    constructor(public basket: Basket)
    {
        RefWatcher.Watch([
            this.basket.Discounter.Discounts,
            ...this.basket.Discounter.Discounts.Map(x => x.IsOn),
            this.DeliveryForm,
            this.PaymentForm,
            this.ReturnForm,
            basket.Items
        ], () =>
        {
            // 🪙 Crypto
            const cryptoDiscountCode = new CryptoDiscount().Code.value;
            this.AllowCryptoPayment.value = this.basket.Discounter.IsOn(cryptoDiscountCode);

            // 🎓 Training
            const selfTrainingDiscountCode = new SelfTrainingDiscount().Code.value;
            this.WithTraining.value = !basket.Discounter.IsOn(selfTrainingDiscountCode);

            this.TrainingInfo.value = this.IsPersonalDelivery
                ? this.WithTraining.value
                    ? "Szkolenie odbędzie się na miejscu podczas odbioru sprzętu. Otrzymasz także materiały do samodzielnej nauki w formie samouczka online"
                    : "Zamiast szkolenia na miejscu otrzymasz materiały do samodzielnej nauki w formie samouczka online"
                : this.WithTraining.value
                    ? "Szkolenie odbędzie się przez telefon. Otrzymasz także materiały do samodzielnej nauki w formie samouczka online"
                    : "Zamiast szkolenia przez telefon otrzymasz materiały do samodzielnej nauki w formie samouczka. Otrzymasz także dostęp do infolinii technicznej"

            // Package + Cash is not possible, so switch to the next available option
            if (this.IsPackageDelivery && this.PaymentForm.Is(PaymentPresets.AllWithCash))
            {
                this.PaymentForm.value = PaymentPresets.DepositWithBankTransfer_ServiceWithCash;
            }
            // Nothing to rent + Personal delivery + Cash at delivery is not possible, so switch to the next available option
            // if (!basket.IsAnythingToRent && !this.deliveryForm.Is(DeliveryForm.Pocztex) && this.paymentForm.Is(FormPaymentForm.CashAtDelivery))
            // this.paymentForm.value = FormPaymentForm.AllWithCash;

            // 💳 Payment
            if (this.basket.AnythingToRent) 
            {
                if (this.AllowCryptoPayment.value)
                {
                    this.PaymentForm.value = PaymentPresets.AllWithCrypto;
                    this.AllowCashPayment.value = false;
                    this.AllowMixedPayment.value = false;
                }
                else 
                {
                    this.AllowCashPayment.value = this.IsPersonalDelivery;
                    this.AllowMixedPayment.value = true;
                }
            }
            else // Nothing to rent
            {
                this.AllowCryptoPayment.value = true;
                this.AllowCashPayment.value = this.IsPersonalDelivery;
                this.AllowCashAtDelivery.value = this.IsPackageDelivery;
            }

            // 💰 Deposit
            this.DepositPaymentInfo.value = {
                [PaymentPresets.AllWithCash]: `Kaucja zostanie pobrana gotówką podczas odbioru sprzętu`,
                [PaymentPresets.DepositWithBankTransfer_ServiceWithCash]: `Łączna suma kaucji wynosi ${basket.GetDepositsSum()}zł. Płatne z góry przelewem. Szczegóły płatności będą widoczne w kolejnym kroku`,
                [PaymentPresets.DepositWithBlik_ServiceWithCash]: `Łączna suma kaucji wynosi ${basket.GetDepositsSum()}zł. Płatne z góry Blikiem. Szczegóły płatności będą widoczne w kolejnym kroku`,
                [PaymentPresets.AllWithBankTransfer]: `Kwota kaucji zostanie doliczona do kwoty przelewu`,
                [PaymentPresets.AllWithCrypto]: `Kaucja będzie płatna Bitcoinem wraz z całością zamówienia`,
            }[this.PaymentForm.value] || "...";

            // 💰 Deposit return 
            this.DepositReturnInfo.value = {
                [PaymentPresets.AllWithCash]: this.IsPackageReturn ? `Kaucja zostanie zwrócona na konto wskazane w formularzu` : `Kaucja zostanie zwrócona na miejscu w gotówce`,
                [PaymentPresets.AllWithBankTransfer]: `Kaucja zostanie zwrócona na konto, z którego przyszła`,
                [PaymentPresets.DepositWithBankTransfer_ServiceWithCash]: `Kaucja zostanie odesłana na konto, z którego przyszła`,
                [PaymentPresets.DepositWithBlik_ServiceWithCash]: `Kaucja zostanie odesłana na numer telefonu, z którego przyszła`,
                [PaymentPresets.AllWithCrypto]: `Kaucja zostanie zwrócona na wskazany portfel (wyliczona z proporcji)`,
            }[this.PaymentForm.value] || "...";

            this.ShowCustomerDepositAccountInput.value = this.IsPersonalDelivery 
            && !this.IsPersonalReturn 
            && this.PaymentForm.IsIn([PaymentPresets.AllWithCash, PaymentPresets.AllWithCrypto]);

            this.IsValid.value = this.basket.Items.IsNotEmpty
                && this.DeliveryForm.IsNot(DeliveryForm.Unset)
                && this.ReturnForm.IsNot(ReturnForm.Unset)
                && this.PaymentForm.IsNot(PaymentPresets.Unset);
        });
    }

    public get IsPersonalDelivery(): boolean
    {
        return this.DeliveryForm.Is(DeliveryForm.Personal);
    }
    public get IsPackageDelivery(): boolean
    {
        return this.DeliveryForm.Is(DeliveryForm.Package);
    }
    public get IsPackageReturn(): boolean
    {
        return this.ReturnForm.Is(ReturnForm.Package);
    }
    public get IsPersonalReturn(): boolean
    {
        return this.ReturnForm.Is(ReturnForm.Personal);
    }
}
